import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import { mediaUrl } from '../utils/blogUtils';
import { compressImageForUpload, isImageFile } from '../utils/imageUpload';
import './ClubPlayerEdit.css';

const ACHIEVEMENT_TYPES = [
  { value: 'GOAL', label: 'Goal(s)' },
  { value: 'ASSIST', label: 'Assist(s)' },
  { value: 'MOTM', label: 'Man of the Match' },
  { value: 'HAT_TRICK', label: 'Hat-trick' },
  { value: 'CLEAN_SHEET', label: 'Clean sheet' },
  { value: 'CUSTOM', label: 'Custom award' },
];

const formatAchievementType = (type) =>
  ACHIEVEMENT_TYPES.find((t) => t.value === type)?.label || type;

const formatGameStatLine = (game) => {
  const parts = [];
  if (game.goals > 0) parts.push(`${game.goals}G`);
  if (game.assists > 0) parts.push(`${game.assists}A`);
  if (game.saves > 0) parts.push(`${game.saves}SV`);
  if (game.defended > 0) parts.push(`${game.defended}DEF`);
  return parts.length > 0 ? parts.join(', ') : 'Played';
};

const formatTeamSide = (side) => (side === 'RED' ? 'Red' : side === 'BLUE' ? 'Blue' : side || '—');

const ClubPlayerEdit = () => {
  const { id: clubId, playerId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    position: 'MIDFIELDER',
    skillLevel: 5,
    skillCategory: '',
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [achievementForm, setAchievementForm] = useState({
    type: 'MOTM',
    eventName: '',
    description: '',
    eventDate: '',
    quantity: '',
  });
  const [addingAchievement, setAddingAchievement] = useState(false);

  const loadAchievements = useCallback(async () => {
    if (!user?.id) return;
    try {
      setAchievementsLoading(true);
      const data = await api.getPlayerAchievements(clubId, playerId, user.id);
      setAchievements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load achievements:', err);
      setAchievements([]);
    } finally {
      setAchievementsLoading(false);
    }
  }, [clubId, playerId, user?.id]);

  const loadProfile = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const data = await api.getPlayerClubProfile(clubId, playerId, user.id);
      if (!data.canEdit) {
        setError('You do not have permission to edit this profile.');
        setProfile(null);
        return;
      }
      setProfile(data);
      const player = data.player;
      setFormData({
        name: player.name || '',
        email: player.email || '',
        phoneNumber: player.phoneNumber || '',
        dateOfBirth: player.dateOfBirth ? player.dateOfBirth.slice(0, 10) : '',
        position: player.position || 'MIDFIELDER',
        skillLevel: player.skillLevel ?? 5,
        skillCategory: player.skillCategory || '',
      });
    } catch (err) {
      setProfile(null);
      setError(err.message || 'Failed to load player');
    } finally {
      setLoading(false);
    }
  }, [clubId, playerId, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    loadProfile();
    loadAchievements();
  }, [isAuthenticated, user, loadProfile, loadAchievements]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'skillLevel' ? parseInt(value, 10) : value,
    }));
    setError('');
    setSuccess('');
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isImageFile(file)) {
      setError('Please select an image file (JPG, PNG, GIF, or WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be 10 MB or smaller.');
      return;
    }
    setProfilePictureFile(file);
    setProfilePreviewUrl(URL.createObjectURL(file));
    setError('');
    setSuccess('');
  };

  const handleAchievementChange = (e) => {
    const { name, value } = e.target;
    setAchievementForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleAddAchievement = async (e) => {
    e.preventDefault();
    if (!user?.id) return;

    try {
      setAddingAchievement(true);
      setError('');
      setSuccess('');

      const payload = {
        type: achievementForm.type,
        eventName: achievementForm.eventName.trim(),
        description: achievementForm.description.trim() || null,
        eventDate: achievementForm.eventDate,
      };
      if (['GOAL', 'ASSIST', 'HAT_TRICK'].includes(achievementForm.type) && achievementForm.quantity) {
        payload.quantity = parseInt(achievementForm.quantity, 10);
      }

      await api.addPlayerAchievement(clubId, playerId, user.id, payload);
      setSuccess('Achievement added successfully.');
      setAchievementForm({
        type: 'MOTM',
        eventName: '',
        description: '',
        eventDate: '',
        quantity: '',
      });
      loadAchievements();
    } catch (err) {
      setError(err.message || 'Failed to add achievement');
    } finally {
      setAddingAchievement(false);
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    if (!user?.id || !window.confirm('Delete this achievement?')) return;
    try {
      setError('');
      await api.deletePlayerAchievement(clubId, playerId, achievementId, user.id);
      setSuccess('Achievement removed.');
      loadAchievements();
    } catch (err) {
      setError(err.message || 'Failed to delete achievement');
    }
  };

  const showQuantityField = ['GOAL', 'ASSIST', 'HAT_TRICK'].includes(achievementForm.type);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id || !profile?.player) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      let profilePictureUrl = profile.player.profilePictureUrl || null;
      if (profilePictureFile) {
        const uploadFile = await compressImageForUpload(profilePictureFile);
        profilePictureUrl = await api.uploadPlayerProfilePicture(uploadFile);
      }

      const payload = {
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber || '',
        dateOfBirth: formData.dateOfBirth || null,
        position: formData.position,
        skillLevel: formData.skillLevel,
        skillCategory: formData.skillCategory || null,
        ...(profilePictureUrl != null && { profilePictureUrl }),
      };

      await api.updateClubPlayer(clubId, playerId, user.id, payload);
      setSuccess('Player profile updated successfully.');
      setProfilePictureFile(null);
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
      setProfilePreviewUrl(null);
      setTimeout(() => {
        navigate(soccerPath(`/clubs/${clubId}/players/${playerId}`));
      }, 800);
    } catch (err) {
      setError(err.message || 'Failed to update player');
    } finally {
      setSaving(false);
    }
  };

  const editingAsClubAdmin = profile?.canEdit && !profile?.ownProfile;

  const currentPhotoUrl = profilePreviewUrl
    || (profile?.player?.profilePictureUrl ? mediaUrl(profile.player.profilePictureUrl) : null);

  if (!isAuthenticated) {
    return (
      <div className="club-player-edit">
        <div className="alert alert-warning">Please log in to edit player profiles.</div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error && !profile) {
    return (
      <div className="club-player-edit">
        <button
          type="button"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}/admin`))}
          className="btn btn-secondary"
        >
          ← Back to Club Admin
        </button>
        <div className="alert alert-error" style={{ marginTop: '1rem' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="club-player-edit">
      <div className="club-player-edit__nav">
        <button
          type="button"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}/players/${playerId}`))}
          className="btn btn-secondary"
        >
          ← Back to Profile
        </button>
        <Link to={soccerPath(`/clubs/${clubId}/admin`)} className="btn btn-secondary">
          Club Admin
        </Link>
      </div>

      <div className="club-player-edit__card">
        <h1>Edit Player Profile</h1>
        <p className="club-player-edit__subtitle">
          {profile?.clubName} — {profile?.player?.name}
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit} className="club-player-edit__form">
          <div className="club-player-edit__photo-section">
            <label>Profile photo</label>
            <div className="club-player-edit__photo-row">
              {currentPhotoUrl ? (
                <img src={currentPhotoUrl} alt="Profile preview" className="club-player-edit__photo" />
              ) : (
                <div className="club-player-edit__photo-placeholder">
                  {formData.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleProfilePictureChange}
                />
                <p className="form-help">JPG, PNG, GIF, or WebP. Large photos are resized automatically. Max 10 MB.</p>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input id="name" type="text" name="name" value={formData.name} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email{editingAsClubAdmin ? '' : ' *'}</label>
            {editingAsClubAdmin ? (
              <>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={profile?.player?.email || ''}
                  readOnly
                  className="club-player-edit__readonly"
                />
                <p className="form-help">
                  Login email is managed by the player and cannot be changed here.
                </p>
              </>
            ) : (
              <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone number</label>
            <input id="phoneNumber" type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of birth</label>
            <input id="dateOfBirth" type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label htmlFor="position">Position *</label>
            <select id="position" name="position" value={formData.position} onChange={handleChange} required>
              <option value="GOALKEEPER">Goalkeeper</option>
              <option value="DEFENDER">Defender</option>
              <option value="MIDFIELDER">Midfielder</option>
              <option value="FORWARD">Forward</option>
              <option value="WINGER">Winger</option>
              <option value="FLEXIBLE">Flexible</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="skillLevel">Skill level * (1–10): {formData.skillLevel}</label>
            <input
              id="skillLevel"
              type="range"
              name="skillLevel"
              min="1"
              max="10"
              value={formData.skillLevel}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="skillCategory">Experience level</label>
            <select id="skillCategory" name="skillCategory" value={formData.skillCategory} onChange={handleChange}>
              <option value="">— Select —</option>
              <option value="BEGINNER">Beginner</option>
              <option value="INTERMEDIATE">Intermediate</option>
              <option value="EXPERT">Expert</option>
            </select>
          </div>

          <div className="club-player-edit__actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(soccerPath(`/clubs/${clubId}/players/${playerId}`))}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {profile?.playingRecord && (
        <div className="club-player-edit__card club-player-edit__game-stats">
          <h2>Game Statistics</h2>
          <p className="club-player-edit__achievements-help">
            Stats are updated automatically when practice scrimmage results are marked final.
            Use manual records below for pickup games and awards not tracked in scrimmage stats.
          </p>

          <div className="club-player-edit__stats-grid">
            <div className="club-player-edit__stat-card">
              <span className="club-player-edit__stat-value">
                {profile.playingRecord.stats?.totalGoals ?? 0}
              </span>
              <span className="club-player-edit__stat-label">Goals</span>
            </div>
            <div className="club-player-edit__stat-card">
              <span className="club-player-edit__stat-value">
                {profile.playingRecord.stats?.totalAssists ?? 0}
              </span>
              <span className="club-player-edit__stat-label">Assists</span>
            </div>
            <div className="club-player-edit__stat-card">
              <span className="club-player-edit__stat-value">
                {profile.playingRecord.stats?.manOfMatchCount ?? 0}
              </span>
              <span className="club-player-edit__stat-label">MOTM</span>
            </div>
            <div className="club-player-edit__stat-card">
              <span className="club-player-edit__stat-value">
                {profile.playingRecord.stats?.practicesPlayed ?? 0}
              </span>
              <span className="club-player-edit__stat-label">Practices</span>
            </div>
          </div>

          {profile.playingRecord.gameLog?.length > 0 ? (
            <div className="club-player-edit__game-log">
              <h3>Game-by-game log</h3>
              <ul>
                {profile.playingRecord.gameLog.map((game) => (
                  <li key={`${game.practiceId}-${game.teamSide}`} className="club-player-edit__game-log-item">
                    <div>
                      <strong>{game.practiceTitle}</strong>
                      <span className="club-player-edit__game-log-meta">
                        {game.practiceDate
                          ? new Date(game.practiceDate).toLocaleDateString()
                          : ''}{' '}
                        · {formatTeamSide(game.teamSide)} Team
                        {game.scoreRed != null && game.scoreBlue != null
                          ? ` · Red ${game.scoreRed}–${game.scoreBlue} Blue`
                          : ''}
                      </span>
                      <p className="club-player-edit__game-log-stats">{formatGameStatLine(game)}</p>
                    </div>
                    <Link
                      to={soccerPath(`/practices/${game.practiceId}/scrimmage-stats`)}
                      className="btn btn-secondary btn-sm"
                    >
                      View game
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="club-player-edit__achievements-empty">
              No finalized scrimmage stats yet for this player.
            </p>
          )}

          <Link
            to={soccerPath(`/clubs/${clubId}/players/${playerId}`)}
            className="btn btn-secondary"
          >
            View public profile
          </Link>
        </div>
      )}

      <div className="club-player-edit__card club-player-edit__achievements">
        <h2>Manual Playing Records</h2>
        <p className="club-player-edit__achievements-help">
          Log pickup games, awards, and events that are not tracked automatically.
          These appear on the player&apos;s profile stats and highlights.
        </p>

        <form onSubmit={handleAddAchievement} className="club-player-edit__achievement-form">
          <div className="club-player-edit__achievement-grid">
            <div className="form-group">
              <label htmlFor="achievement-type">Type *</label>
              <select
                id="achievement-type"
                name="type"
                value={achievementForm.type}
                onChange={handleAchievementChange}
                required
              >
                {ACHIEVEMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="achievement-date">Event date *</label>
              <input
                id="achievement-date"
                type="date"
                name="eventDate"
                value={achievementForm.eventDate}
                onChange={handleAchievementChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="achievement-event">Event name *</label>
            <input
              id="achievement-event"
              type="text"
              name="eventName"
              value={achievementForm.eventName}
              onChange={handleAchievementChange}
              placeholder="e.g. Tuskers Thursday Pickup Soccer"
              required
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="achievement-desc">Description</label>
            <textarea
              id="achievement-desc"
              name="description"
              value={achievementForm.description}
              onChange={handleAchievementChange}
              placeholder="e.g. Scored hat-trick + assists. Man of the Match."
              rows={2}
              maxLength={500}
            />
          </div>

          {showQuantityField && (
            <div className="form-group">
              <label htmlFor="achievement-quantity">
                {achievementForm.type === 'HAT_TRICK' ? 'Goals scored (optional)' : 'Count (optional)'}
              </label>
              <input
                id="achievement-quantity"
                type="number"
                name="quantity"
                min="1"
                max="99"
                value={achievementForm.quantity}
                onChange={handleAchievementChange}
                placeholder={achievementForm.type === 'HAT_TRICK' ? '3' : '1'}
              />
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={addingAchievement}>
            {addingAchievement ? 'Adding...' : 'Add Achievement'}
          </button>
        </form>

        <div className="club-player-edit__achievement-list">
          <h3>Recorded achievements</h3>
          {achievementsLoading && <p>Loading achievements...</p>}
          {!achievementsLoading && achievements.length === 0 && (
            <p className="club-player-edit__achievements-empty">No manual records yet.</p>
          )}
          {!achievementsLoading && achievements.length > 0 && (
            <ul>
              {achievements.map((item) => (
                <li key={item.id} className="club-player-edit__achievement-item">
                  <div>
                    <strong>{formatAchievementType(item.type)}</strong>
                    <span className="club-player-edit__achievement-date">
                      {item.eventDate ? new Date(item.eventDate).toLocaleDateString() : ''}
                    </span>
                    <p className="club-player-edit__achievement-event">{item.eventName}</p>
                    {item.description && (
                      <p className="club-player-edit__achievement-desc">{item.description}</p>
                    )}
                    {item.quantity > 1 && (
                      <span className="club-player-edit__achievement-qty">×{item.quantity}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteAchievement(item.id)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubPlayerEdit;
